import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    Grid,
    IconButton,
    makeStyles,
    Modal,
    Paper,
    Typography,
} from "@material-ui/core";
import useStyles from '../styles/styles';
import useTrafficStyles from '../styles/trafficStyles';
import {Alert} from '@material-ui/lab';
import {useLazyQuery, useMutation, useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import ClearIcon from '@material-ui/icons/Clear';
import CheckIcon from '@material-ui/icons/Check';
import theme from '../styles/theme';
import {eventPayoutFields, trafficSourceFields} from '../utils/TrafficSourceMap';
import useFormInputs from "../hooks/useFormInputs";
import _ from 'lodash';
import {navigate} from '@reach/router';
import PanelBox from '../components/TrafficSource/PanelBox';
import RenderInput from '../components/TrafficSource/RenderInput';
import AddBoxIcon from "@material-ui/icons/AddBox";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import useFilterData from "../hooks/useFilterData";

const useAddRevenueStyles = makeStyles({
    root: {
        border: '1px solid',

        '& .MuiExpansionPanelSummary-root': {
            padding: theme.spacing(0, 2, 0, 1),
        },
    },
    removeButton: {
        cursor: 'pointer',
        pointerEvents: 'all'
    },
});

const AddTrafficSource = ({ offerId }) => {
    const classes = useStyles();
    const trafficClasses = useTrafficStyles();
    const scheduleClasses = useAddRevenueStyles();
    const [isSaved, setIsSaved] = useState(false);
    const [errors, setErrors] = useState([]);
    const [expandedPanelsArr, setExpandedPanelsArr] = useState([]);
    const [defaultValues, setDefaultValues] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [manualLoad, setManualLoad] = useState(false);
    const [matchPromo, setMatchPromo] = useState([]);
    const [eventPayoutRows, setEventPayoutRows] = useState([]);
    const [selectedOffer, setSelectedOffer] = useState("");

    const onSubmit = () => {

        let error = false
        const errorMessages = []
        setErrors(error);
        let nonRequiredFields = [];
        trafficSourceFields.forEach((section) => {
            section.arr.forEach((item) => {
                if (!item.required) {
                    nonRequiredFields.push(item.key);
                }
            });
        });
        Object.keys(inputs).forEach(key => {
            if (nonRequiredFields.includes(key)) {
                return
            }

            const value = inputs[key]

            if (value === '') {
                console.error(`${key} is a required field.`)
                error = true
                errorMessages[0] = 'Please add values to the highlighted fields!'
            }
        });

        const offer = findOfferById(data.allOffer, inputs["offerId"])
        if(offer && offer.multiOffer){
            if(!findEventPayoutRowWithConversionRevShare()){
                console.error(`Required event payout not found for attached offer that is multiOffer`)
                error = true
                errorMessages[1] = 'Please add a Conversion - Rev Share event payout because of the attached offer being multiOffer!'
            }

            if(inputs['payoutStrategy'] !== 'EVENT_PAYOUT'){
                console.error(`Required payout strategy needs to be EVENT_PAYOUT`)
                error = true
                errorMessages[2] = 'Please set the Payout Strategy to EVENT_PAYOUT for the attached offer being multiOffer!'
            }
        }

        if(inputs['pinpoint'] === true && !(inputs['medium'] === 'SMS' || inputs['medium'] === 'EMAIL')) {
            error = true
            errorMessages[3] = 'Medium must be SMS or EMAIL for Pinpoint Traffic Sources'
        }

        if (!error) {
            runQuery();
            openModal();
        } else {
            setErrors(errorMessages)
        }
    };
    const {
        inputs,
        handleSwitchChange,
        handleFilterChange,
        handleInputChange,
        handleSubmit,
        setInputs
    } = useFormInputs(onSubmit);

    const findOfferById = useCallback((allOffer, offerId) => {
        return allOffer.find(offer => offer.id === offerId)
    }, [])

    const [runQuery, {loading: tsLoading, error: tsError, data: tsData,}] = useLazyQuery(gql`
        query getTrafficSourceData {
            allTrafficSource {
                nodes {
                    id
                    name
                    affiliate {
                        id
                        name
                    }
                    legacyPromoId
                }
            }
        }
    `);

    const {loading, error, data, networkStatus} = useQuery(gql`
        query AddTrafficSourcePage {
            allAffiliate {
                nodes {
                    id
                    name
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                    legacyPromoId
                }
            }
            allPingTree {
                nodes {
                    id
                }
            }
            allAffiliatePayoutStrategy {
                nodes {
                    id
                    enum
                    name
                }
            }
            allCompanies {
                id
                name
            }
            allTrafficOrigin: __type(name: "TrafficOrigin") {
                enumValues {
                    name
                }
            }
            allTrafficChannels: __type(name: "TrafficChannel") {
                enumValues {
                    name
                }
            }
            allTrafficMedium: __type(name: "TrafficMedium") {
                enumValues {
                    name
                }
            }
            allFeeds: __type(name: "ListFeed") {
                enumValues {
                    name
                }
            }
            allRevenueEvents: __type(name: "RevenueEvent") {
                enumValues {
                    name
                }
            }
            allEventPayoutStrategyTypes: __type(name: "EventPayoutStrategyType") {
                enumValues {
                    name
                }
            }
            allPayoutTypes: __type(name: "PayoutType") {
                enumValues {
                    name
                }
            }
            allDataPackageTypes: __type(name: "DataPackageType") {
                enumValues {
                    name
                }
            }
            allField: __type(name: "Field") {
                enumValues {
                    name
                }
            }
            allTrafficSourceSelectionStrategy: __type(name: "TrafficSourceSelectionStrategy") {
                enumValues {
                    name
                }
            }
            allAdvertiser {
                id
                name
            }
            allOffer {
                id
                offerName
                advertiserId
                multiOffer
                offerUrls {
                    httpMethod
                    id
                    offerId
                    name
                    urlPath
                }
            }
        }

    `, {
        notifyOnNetworkStatusChange: true,
    });

    const [createTrafficSource, {data: mutationData, loading: mutationLoading, error: mutationError}] = useMutation(gql`
        mutation CreateTrafficSource(
            $name: String!
            $legacyZoneId: Int
            $legacyPromoId: Int
            $legacyOfferId: Int
            $origin: TrafficOrigin!
            $medium: TrafficMedium!
            $channel: TrafficChannel!
            $affiliateId: UUID!
            $internal: Boolean!
            $includeS3InSource: Boolean!
            $minimumPrice: BigDecimal
            $maximumPrice: BigDecimal
            $timeout: Int
            $sourceUrl: String
            $offerUrl: String
            $isListManageable: Boolean!
            $soldFeed: ListFeed!
            $unsoldFeed: ListFeed!
            $aggregatorOnly: Boolean!
            $rootPingTree: String!
            $isTest: Boolean!
            $proxyToOlp: Boolean!
            $payoutStrategy: PayoutStrategy!
            $splitTest: Boolean!
            $priceRejectMins: PriceRejectMinsInput!
            $eventPayouts: [EventPayoutMutatorInput!]
            $multiTierSubmit: Boolean!
            $offerId: UUID!
            $pinpoint: Boolean!
            $multiConversion: Boolean!
            $targetOfferUrlId: UUID
            $trafficSourceSelectionStrategy: TrafficSourceSelectionStrategy!
            $exclusive: Boolean!
            $multiOffer: Boolean!
        ) {
            createTrafficSource(
                name: $name
                legacyZoneId: $legacyZoneId
                legacyPromoId: $legacyPromoId
                legacyOfferId: $legacyOfferId
                origin: $origin
                medium: $medium
                channel: $channel
                affiliateId: $affiliateId
                internal: $internal
                includeS3InSource: $includeS3InSource
                minimumPrice: $minimumPrice
                maximumPrice: $maximumPrice
                timeout: $timeout
                sourceUrl: $sourceUrl
                offerUrl: $offerUrl
                isListManageable: $isListManageable
                soldFeed: $soldFeed
                unsoldFeed: $unsoldFeed
                aggregatorOnly: $aggregatorOnly
                rootPingTree: $rootPingTree
                isTest: $isTest
                payoutStrategy: $payoutStrategy
                proxyToOlp: $proxyToOlp,
                splitTest: $splitTest
                priceRejectMins: $priceRejectMins
                eventPayouts: $eventPayouts
                multiTierSubmit: $multiTierSubmit
                offerId: $offerId
                pinpoint: $pinpoint
                multiConversion: $multiConversion
                targetOfferUrlId: $targetOfferUrlId
                trafficSourceSelectionStrategy: $trafficSourceSelectionStrategy
                exclusive: $exclusive
                multiOffer: $multiOffer
            ) {
                id
                name
                aggregatorOnly
                channel
                createdAt
                everflowAffiliateHash
                includeS3InSource
                internal
                isListManageable
                isTest
                maximumPrice
                medium
                minimumPrice
                origin
                rootPingTree
                soldFeed
                sourceUrl
                offerUrl
                timeout
                unsoldFeed
                splitTest
                affiliate {
                    id
                    name
                }
                payoutStrategy {
                    id
                    enum
                    name
                }
                proxyToOlp
                legacyZoneId
                legacyOfferId
                legacyPromoId
                priceRejectMins {
                    mins
                    config {
                        noPennies
                        checkNextTier
                        returnAnyBid
                    }
                }
                eventPayouts {
                    id
                    trafficSourceId
                    event
                    payoutStrategy
                    payoutType
                    payout
                    field
                    dataPackageType
                    companyId
                    billed
                    revShare
                }
                offer {
                    id
                    advertiserId
                    offerName
                    offerUrls {
                        id
                        active
                        url
                        name
                        httpMethod
                        offerId
                        offerUrlQueryParameters {
                            constantValue
                            id
                            offerUrlId
                            parameterName
                            sourceData
                        }
                    }
                    offerNote {
                        id
                        notes
                        offerId
                    }
                }
                pinpoint
                multiConversion
                multiOffer
                targetOfferUrlId
                trafficSourceSelectionStrategy
                exclusive
            }
        }
    `);

    useEffect(() => {
        if (data && Object.keys(data).length) {
            const inputOfferId = inputs["offerId"]
            if (inputOfferId) {
                if (selectedOffer !== inputOfferId) {
                    setSelectedOffer(inputOfferId)
                }
            }
        }
    }, [inputs])

    useEffect(() => {
        if (data && Object.keys(data).length) {
            const offer = findOfferById(data.allOffer, selectedOffer)
            if(offer && offer.multiOffer){
                if(!findEventPayoutRowWithConversionRevShare())
                {
                    addEventPayoutRowWithEventAndPayoutType("CONVERSION", "REV_SHARE")
                }

                setInputs(inputs => ({
                    ...inputs,
                    payoutStrategy: "EVENT_PAYOUT"
                }));
            }
        }
    }, [selectedOffer])

    useEffect(() => {
        if (tsData && Object.keys(tsData).length && isModalOpen) {
            const arr = tsData.allTrafficSource.nodes.filter(item => {
                if (item.legacyPromoId && inputs.legacyPromoId) {
                    return item.legacyPromoId.toString() === inputs.legacyPromoId;
                }
            });
            setMatchPromo(arr);
        }
    }, [tsData, isModalOpen]);

    useEffect(() => {
        if (isSaved) {
            setTimeout(() => setIsSaved(false), 5000);
        }
    }, [isSaved]);

    useEffect(() => {
        if (data && Object.keys(data).length) {
            //Set the fields
            trafficSourceFields.forEach((section => {
                section.arr.forEach((item => {
                    const {key, type} = item
                    if (inputs[item.key] === undefined) {
                        let value = "";

                        if (type === 'switch' && value === "") {
                            value = false;
                        }

                        if (_.isObject(value)) {
                            value = value.enum;
                        }

                        setInputs(inputs => ({
                            ...inputs,
                            [key]: value
                        }));
                        setDefaultValues(inputs => ({
                            ...inputs,
                            [key]: value
                        }));
                    }
                }));
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);


    const findEventPayoutRowWithConversionRevShare = () => {
        const eventPayout = eventPayoutRows.find(row => row[0].value === "CONVERSION" && row[2].value === "REV_SHARE")
        if(!eventPayout){
            return null
        }
        return eventPayout
    }

    const openModal = () => {
        setIsModalOpen(true);
    };
    const closeModal = () => {
        setIsModalOpen(false);
    };

    const addEventPayoutRow = () => {
        const values = [...eventPayoutRows];
        const newSet = eventPayoutFields.map(item => {
            return {
                ...item,
                value: '',
            };
        });
        values.push(newSet);
        setEventPayoutRows(values);
    };

    const addEventPayoutRowWithEventAndPayoutType = (event, payoutType) => {
        const values = [...eventPayoutRows];

        if(!data.allRevenueEvents.enumValues.find(e => e.name === event) || !data.allPayoutTypes.enumValues.find(t => t.name === payoutType)){
            return
        }

        const newSet = eventPayoutFields.map(item => {
            let valueToSet = ''
            if(item.key === "event"){
                valueToSet = event
            }else if(item.key === "payoutType"){
                valueToSet = payoutType
            }
            return {
                ...item,
                value: valueToSet,
            };
        });
        values.push(newSet);
        setExpandedPanelsArr(expandedPanelsArr => [...expandedPanelsArr, values.length-1]);
        setEventPayoutRows(values);
    };

    const deleteRow = (deleteIndex) => {
        const values = eventPayoutRows.filter((item, index) => index !== deleteIndex)
        setEventPayoutRows(values);
    };

    const findOptionId = (options, name) => {
        const option = options.find(item => item.name === name)
        return option ? option.id : null
    }

    const handleEventPayoutChanges = (idx, rowIdx, newVal, type) => {

        let values = [...eventPayoutRows];
        const obj = [...values[idx]];
        const key = obj[rowIdx].key;

        if (type === 'dateTime') {
            obj[rowIdx] = {
                ...obj[rowIdx],
                value: newVal.toISOString()
            };
        } else if (key === 'companyId') {
            const options = data[obj[rowIdx].val]
            const optionId = findOptionId(options, newVal)
            obj[rowIdx] = {
                ...obj[rowIdx],
                value: optionId
            };
        } else {
            obj[rowIdx] = {
                ...obj[rowIdx],
                value: newVal
            };
        }
        values[idx] = [...obj];
        setEventPayoutRows(values);
    }

    useEffect(() => {
        if (mutationLoading) {
            closeModal();
        }
    }, [mutationLoading]);

    useEffect(() => {
        if (mutationData && Object.keys(mutationData).length) {
            if (offerId) {
                setTimeout(() => window.location = (`/info/offers/${offerId}`), 3000);
            } else {
                setTimeout(() => window.location = (`/info/traffic-sources/${mutationData.createTrafficSource.id}`), 3000);
            }
        }
    }, [mutationData])

    if (loading || networkStatus === 4 || mutationLoading) {
        return (
            <Backdrop className={classes.backdrop} open={loading || mutationLoading || manualLoad}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        );
    }

    if (error) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
                <p>
                    <a href="/#" onClick={(e) => {
                        e.preventDefault();
                        navigate('/info/traffic-sources');
                    }}>Click here to view all traffic sources.</a>
                </p>
            </Paper>
        );
    }

    const RowChanges = ({rowData}) => {
        const {title, arr} = rowData;
        return (
            <Container maxWidth={false} disableGutters className={trafficClasses.rowDiff}>
                {title && (
                    <Box className={trafficClasses.rowDiffSectionHeader}>
                        <Typography variant="body1">
                            <strong>{title}</strong>
                        </Typography>
                    </Box>
                )}

                <Box>
                    {arr.map(item => {
                        const {key, label, type} = item;
                        const newInputKey = inputs[key];
                        const currentInputKey = defaultValues[key];
                        return (
                            <Grid container key={key}
                                  className={`row-grid ${newInputKey !== currentInputKey ? trafficClasses.rowDiffHighlight : ''}`}>
                                {label ? (
                                    <Grid item xs={4}>
                                        <Typography variant="body">
                                            {label}
                                        </Typography>
                                    </Grid>
                                ) : null}
                                <Grid item xs={4}>
                                    {type === 'switch' ? (
                                        <>
                                            {currentInputKey ? (
                                                <CheckIcon style={{color: theme.palette.success.main}}/>
                                            ) : (
                                                <ClearIcon style={{color: theme.palette.error.main}}/>
                                            )}
                                        </>
                                    ) : (
                                        <span>{_.isNil(currentInputKey) || currentInputKey === '' ? 'N/A' : currentInputKey}</span>
                                    )}
                                </Grid>
                                <Grid item xs={4}>
                                    {type === 'switch' ? (
                                        <>
                                            {newInputKey ? (
                                                <CheckIcon style={{color: theme.palette.success.main}}/>
                                            ) : (
                                                <ClearIcon style={{color: theme.palette.error.main}}/>
                                            )}
                                        </>
                                    ) : (
                                        <span>{_.isNil(newInputKey) || newInputKey === '' ? 'N/A' : newInputKey}</span>
                                    )}
                                </Grid>
                            </Grid>
                        );
                    })}
                </Box>
            </Container>
        );
    };
    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace}>
                Add New Traffic Source
            </Typography>
            {!loading && mutationError ? (
                <Paper className={classes.paper}>
                    <Alert severity="error">{JSON.stringify(mutationError.message.split(`\n`)[0], null, 2).replace('\"', '')}</Alert>
                </Paper>
            ) : null}
            <Paper className={classes.paper}>
                <Container maxWidth={false} className={trafficClasses.root}>
                    <form id="traffic-source-form">
                        {isSaved ? (
                            <Alert severity="success">Updates Saved!</Alert>
                        ) : false}
                        {errors && errors.length ? errors.map((error) => {
                                return (<Alert severity="error">{error}</Alert>)
                            }
                        ) : false}
                        <Box className={trafficClasses.headerBox}>
                            <Grid container spacing={3} alignItems="flex-end">
                                {trafficSourceFields.map((section, idx) => {
                                    const {title, arr} = section;
                                    if (!title) {
                                        return (
                                            <Fragment key={idx.toString()}>
                                                {arr.map(item => {
                                                    const {key, label} = item;
                                                    return (
                                                        <Grid key={key} item xs={4} className={trafficClasses.header}>
                                                            <Typography variant="body1">
                                                                <strong>{label}</strong>
                                                            </Typography>
                                                            <RenderInput
                                                                item={item}
                                                                value={inputs[key]}
                                                                data={data}
                                                                handleFilterChange={handleFilterChange}
                                                                handleInputChange={handleInputChange}
                                                                handleSwitchChange={handleSwitchChange}
                                                                setInputs={setInputs}
                                                                inputs={inputs}
                                                            />
                                                        </Grid>
                                                    );
                                                })}
                                            </Fragment>
                                        );
                                    }
                                    return null;
                                })}
                                <Grid item xs={4}
                                      className={`${trafficClasses.header} ${trafficClasses.headerButtons}`}>
                                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                                        Create New
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box>
                            <Grid container spacing={3} direction="row" justify="center" alignItems="stretch">
                                {trafficSourceFields.map((section, idx) => {
                                    if(section.title === "LEGACY"){
                                        const row = section.arr.find(row => row.key === "legacyPromoId")
                                        row.disabled = true
                                    }
                                    return (
                                    section.title &&
                                    <PanelBox
                                        key={`${section.title}-${idx.toString()}`}
                                        panelData={section}
                                        trafficClasses={trafficClasses}
                                        editView={true}
                                        inputs={inputs}
                                        data={data}
                                        handleFilterChange={handleFilterChange}
                                        handleInputChange={handleInputChange}
                                        handleSwitchChange={handleSwitchChange}
                                        setInputs={setInputs}
                                    />
                                )
                                })}
                            </Grid>
                        </Box>

                        <Divider/>
                        <Box className={`${classes.filterBox} ${trafficClasses.panelBox}`} style={{marginTop: 0}}>
                            <Chip
                                label={'Event Payouts'}
                                className={classes.filterSectionLabels}
                            />
                            <Button
                                style={{float: 'left'}}
                                variant="contained"
                                className={classes.addButton}
                                onClick={() => addEventPayoutRow()}
                                size="small"
                            >
                                Add Row <AddBoxIcon/>
                            </Button>
                            <Grid container direction="row" alignItems="flex-start" spacing={2}>
                                <Grid item xs={12} style={{padding: '10px 0'}}>
                                    {eventPayoutRows && eventPayoutRows.map((rows, idx) => {
                                        const isExpanded = expandedPanelsArr.length ? expandedPanelsArr.includes(idx) : false;
                                        return (
                                            <ExpansionPanel
                                                key={idx.toString()}
                                                id={`adjustments-${idx}`}
                                                expanded={isExpanded}
                                                className={scheduleClasses.root}
                                                onChange={(e, bool) => {
                                                    if (bool) {
                                                        setExpandedPanelsArr(expandedPanelsArr => [...expandedPanelsArr, idx]);
                                                    } else {
                                                        if (expandedPanelsArr.includes(idx)) {
                                                            setExpandedPanelsArr(expandedPanelsArr.filter(panel => panel !== idx));
                                                        }
                                                    }
                                                }}
                                            >
                                                <ExpansionPanelSummary
                                                    expandIcon={<ExpandMoreIcon/>}
                                                >
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={3}
                                                    >
                                                        <Grid container key={idx.toString()} style={{margin: '10px 0'}}>
                                                            <Grid item xs={12} md={3}>
                                                                <Box display="flex" flexDirection="row"
                                                                     alignItems="center">
                                                                    <Box display="flex" flexDirection="column"
                                                                         alignItems="center">
                                                                        <IconButton
                                                                            className={scheduleClasses.removeButton}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                deleteRow(idx);
                                                                            }}>
                                                                            <RemoveCircleIcon/>
                                                                        </IconButton>
                                                                    </Box>
                                                                    <Box textAlign="left">
                                                                        <Typography variant="caption">
                                                                            {isExpanded ? 'Close Revenue Event Payout' : 'Edit Revenue Event Payout'}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </ExpansionPanelSummary>
                                                <ExpansionPanelDetails className={classes.details}>
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={3}
                                                    >
                                                        {rows.map((item, rowIdx) => {
                                                            return (
                                                                <Grid item xs={4} key={rowIdx}
                                                                      style={{paddingBottom: '10px'}}>
                                                                    <RenderInput
                                                                        item={item}
                                                                        disabled={item.disabled || false}
                                                                        value={eventPayoutRows[idx][rowIdx].value}
                                                                        data={data}
                                                                        handleFilterChange={(newVal) => handleEventPayoutChanges(idx, rowIdx, newVal, item.type)}
                                                                        handleInputChange={(e) => handleEventPayoutChanges(idx, rowIdx, e.target.value)}
                                                                        handleSwitchChange={handleSwitchChange}
                                                                        setInputs={setInputs}
                                                                        inputs={inputs}
                                                                    />
                                                                    <Divider/>
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                </ExpansionPanelDetails>
                                            </ExpansionPanel>
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        </Box>
                    </form>
                </Container>
                <Modal
                    open={isModalOpen}
                    onClose={closeModal}
                >
                    <Container className={`${classes.modal} ${trafficClasses.modal}`} maxWidth={false}>
                        <Typography variant="h5" align="center" gutterBottom>
                            Please confirm your traffic source changes
                        </Typography>
                        <Paper className={classes.paper} style={{marginBottom: 0}}>
                            {inputs.legacyPromoId ? (
                                <>
                                    {matchPromo.length ? (
                                        <Alert severity={"warning"}>
                                            <Typography variant="body1" style={{marginBottom: '10px'}}>
                                                Your promo code ({inputs.legacyPromoId}) is being used by the below
                                                traffic sources ({matchPromo.length}). This view is scrollable!
                                            </Typography>
                                            <Box style={{height: '200px', overflowY: 'scroll'}}>
                                                {matchPromo.map(item => (
                                                    <>
                                                        <Typography variant="body1" key={`${item.id} - ${item.name}`}
                                                                    style={{margin: '10px 0'}}>
                                                            Affiliate: {item.affiliate.name} - Traffic Source Name:
                                                            - {item.name}
                                                        </Typography>
                                                        <Divider/>
                                                    </>
                                                ))}
                                            </Box>
                                        </Alert>
                                    ) : (
                                        <Alert severity={"success"}>Your promo code has no existing traffic sources
                                            linked to it.</Alert>
                                    )}
                                </>
                            ) : null}
                            <Box>
                                <Grid container className="row-grid">
                                    <Grid item xs={4}/>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1" align="center">
                                            CURRENT CHANGES
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1" align="center">
                                            NEW CHANGES
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box>
                                {trafficSourceFields.map((section, idx) => (
                                    <RowChanges key={section.title} rowData={section}/>
                                ))}
                            </Box>
                            <Grid item xs className={trafficClasses.headerButtons}>
                                <Button variant="contained" color="primary" onClick={(e) => {
                                    e.preventDefault();
                                    const affiliateId = data.allAffiliate.nodes.filter(item => item.name === inputs.affiliateName);
                                    const finalInputs = _.pickBy(inputs, v => v !== '');
                                    setManualLoad(true);
                                    let minsArr = [];
                                    if (finalInputs["priceRejectMins.mins"]) {
                                        const priceRejectMins = inputs["priceRejectMins.mins"].split(',');
                                        priceRejectMins.forEach(item => {
                                            if (item !== ' ') {
                                                minsArr.push(parseFloat(item));
                                            }
                                        });
                                    }
                                    let finalEventPayouts = []
                                    eventPayoutRows.forEach((rows) => {
                                        let obj = {};
                                        rows.forEach((item) => {
                                            if (item.value !== '' && (item.key !== 'id')) {
                                                obj = {
                                                    ...obj,
                                                    [item.key]: item.value
                                                };
                                            } else if (item.key === 'id') {
                                                obj = {
                                                    ...obj,
                                                    id: item.id,
                                                };
                                            }
                                        });
                                        obj = {
                                            ...obj,
                                            delete: false
                                        };
                                        if (Object.keys(obj).length) {
                                            finalEventPayouts.push(obj);
                                        }
                                    });

                                    createTrafficSource({
                                        variables: {
                                            ...finalInputs,
                                            affiliateId: affiliateId.length ? affiliateId[0].id : null,
                                            priceRejectMins: {
                                                mins: minsArr,
                                                config: {
                                                    noPennies: finalInputs["priceRejectMins.config.noPennies"],
                                                    checkNextTier: finalInputs["priceRejectMins.config.checkNextTier"],
                                                    returnAnyBid: finalInputs["priceRejectMins.config.returnAnyBid"],
                                                }
                                            },
                                            eventPayouts: finalEventPayouts
                                        }
                                    });
                                }}>
                                    Save
                                </Button>
                                <Button variant="outlined" onClick={() => closeModal()}>
                                    Cancel
                                </Button>
                            </Grid>
                        </Paper>
                    </Container>
                </Modal>
            </Paper>
        </>
    );
};

export default AddTrafficSource;
